export interface PublicKeyCredentialDescriptorJSON {
  type: PublicKeyCredentialType;
  id: string;
  transports?: AuthenticatorTransport[];
}

export interface PublicKeyCredentialRequestOptionsJSON {
  challenge: string;
  timeout?: number | null;
  rpId?: string | null;
  allowCredentials?: PublicKeyCredentialDescriptorJSON[] | null;
  userVerification?: UserVerificationRequirement | null;
  extensions?: AuthenticationExtensionsClientInputs;
}

export interface PublicKeyCredentialCreationOptionsJSON {
  rp: PublicKeyCredentialRpEntity;
  user: {
    id: string;
    name: string;
    displayName: string;
  };
  challenge: string;
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number | null;
  attestation?: AttestationConveyancePreference | null;
  authenticatorSelection?: AuthenticatorSelectionCriteria | null;
  excludeCredentials?: PublicKeyCredentialDescriptorJSON[] | null;
  extensions?: AuthenticationExtensionsClientInputs;
}

export interface SerializedAttestationCredential {
  id: string;
  type: PublicKeyCredentialType;
  rawId: string;
  response: {
    clientDataJSON: string;
    attestationObject: string;
  };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
}

export interface SerializedAssertionCredential {
  id: string;
  type: PublicKeyCredentialType;
  rawId: string;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
    userHandle?: string;
  };
  clientExtensionResults: AuthenticationExtensionsClientOutputs;
}

export function decodeRequestOptions(json: PublicKeyCredentialRequestOptionsJSON): PublicKeyCredentialRequestOptions {
  return {
    challenge: toBufferSource(json.challenge),
    timeout: nullToUndefined(json.timeout),
    rpId: nullToUndefined(json.rpId),
    allowCredentials: json.allowCredentials?.map((descriptor) => ({
      type: descriptor.type as PublicKeyCredentialType,
      transports: descriptor.transports,
      id: toBufferSource(descriptor.id)
    })),
    userVerification: nullToUndefined(json.userVerification),
    extensions: json.extensions
  };
}

export function decodeCreationOptions(json: PublicKeyCredentialCreationOptionsJSON): PublicKeyCredentialCreationOptions {
  return {
    rp: json.rp,
    user: {
      ...json.user,
      id: toBufferSource(json.user.id)
    },
    challenge: toBufferSource(json.challenge),
    pubKeyCredParams: json.pubKeyCredParams,
    timeout: nullToUndefined(json.timeout),
    attestation: nullToUndefined(json.attestation),
    authenticatorSelection: json.authenticatorSelection ?? undefined,
    excludeCredentials: json.excludeCredentials?.map((descriptor) => ({
      type: descriptor.type as PublicKeyCredentialType,
      transports: descriptor.transports,
      id: toBufferSource(descriptor.id)
    })),
    extensions: json.extensions
  };
}

export function serializeCredential(credential: PublicKeyCredential): SerializedAttestationCredential | SerializedAssertionCredential {
  if (isAttestationResponse(credential.response)) {
    const response = credential.response;
    return {
      id: credential.id,
      type: credential.type as PublicKeyCredentialType,
      rawId: arrayBufferToBase64Url(credential.rawId),
      response: {
        clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
        attestationObject: arrayBufferToBase64Url(response.attestationObject)
      },
      clientExtensionResults: credential.getClientExtensionResults()
    };
  }

  if (isAssertionResponse(credential.response)) {
    const response = credential.response;
    const userHandle = response.userHandle ? arrayBufferToBase64Url(response.userHandle) : undefined;
    return {
      id: credential.id,
      type: credential.type as PublicKeyCredentialType,
      rawId: arrayBufferToBase64Url(credential.rawId),
      response: {
        clientDataJSON: arrayBufferToBase64Url(response.clientDataJSON),
        authenticatorData: arrayBufferToBase64Url(response.authenticatorData),
        signature: arrayBufferToBase64Url(response.signature),
        userHandle
      },
      clientExtensionResults: credential.getClientExtensionResults()
    };
  }

  throw new Error('Unsupported credential response type');
}

export function arrayBufferToBase64Url(buffer: BufferSource): string {
  const view = bufferSourceToUint8Array(buffer);
  let binary = '';
  for (let i = 0; i < view.length; i += 1) {
    binary += String.fromCharCode(view[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '');
}

export function base64UrlToUint8Array(value: string): Uint8Array {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padLength = (4 - (normalized.length % 4)) % 4;
  const padded = normalized + '='.repeat(padLength);
  const binary = atob(padded);
  const output = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    output[i] = binary.charCodeAt(i);
  }
  return output;
}

function nullToUndefined<T>(value: T | null | undefined): T | undefined {
  return value == null ? undefined : value;
}

function isAttestationResponse(response: AuthenticatorResponse): response is AuthenticatorAttestationResponse {
  return 'attestationObject' in response;
}

function isAssertionResponse(response: AuthenticatorResponse): response is AuthenticatorAssertionResponse {
  return 'authenticatorData' in response && 'signature' in response;
}

function toBufferSource(value: string): ArrayBuffer {
  const view = base64UrlToUint8Array(value);
  const buffer = new ArrayBuffer(view.byteLength);
  new Uint8Array(buffer).set(view);
  return buffer;
}

function bufferSourceToUint8Array(input: BufferSource): Uint8Array {
  if (input instanceof ArrayBuffer) {
    return new Uint8Array(input);
  }

  if (ArrayBuffer.isView(input)) {
    return new Uint8Array(input.buffer, input.byteOffset, input.byteLength);
  }

  throw new TypeError('Unsupported buffer source');
}
